// SixDetailService.ts
import type { UpdateManageAccessPayload } from '@/types/manage-access.types';
import type { SixFileFavoriteDTO } from '@/types/SixCatalogDTOs';
import type { SixDetailFilterKey } from '@/types/SixDetail.types';
import type { DeletedFileEntry } from '@/types/poc/deletedFiles';
import type {
  CreateFolderResponseDTO,
  DeleteFileResponseDTO,
  FetchFilesParams,
  MakeFileAvailableToHAPPayloadDTO,
  MakeFileAvailableToHAPResultDTO,
  MoveItemsPayloadDTO,
  ParticipantsPayloadDTO,
  SixActivityDTO,
  SixFilesApiResponse,
  SixFileDTO,
  SixSummaryDTO,
  UpdateParticipantsResponseDTO,
} from '@/types/SixDetailDTOs';

export abstract class SixDetailService {
  /* meta */
  abstract getMeta(id: string, filters?: readonly SixDetailFilterKey[]): Promise<SixSummaryDTO>;

  /* files */
  abstract fetchFiles(id: string, params?: FetchFilesParams): Promise<SixFilesApiResponse>;
  abstract uploadFile(id: string, file: File): Promise<SixFileDTO>;
  /**
   * Hands an already Helia-stored encrypted file off to the backend/HAP
   * replication path. Resolves once the underlying command completes
   * (transparent CQRS polling); rejects on command failure or timeout.
   */
  abstract makeFileAvailableToHAP(
    sixId: string,
    payload: MakeFileAvailableToHAPPayloadDTO
  ): Promise<MakeFileAvailableToHAPResultDTO>;
  abstract createFolder(
    id: string,
    folderName: string,
    folderPath: string
  ): Promise<CreateFolderResponseDTO>;
  abstract deleteFolder(sixId: string, id: string): Promise<DeleteFileResponseDTO>;

  /* activity */
  abstract fetchActivity(id: string): Promise<SixActivityDTO[]>;
  abstract exportActivityCsv(id: string): Promise<Blob>;

  /* participant */
  abstract fetchParticipants(id: string): Promise<ParticipantsPayloadDTO>;
  abstract updateSixParticipants(
    sixId: string,
    payload: UpdateManageAccessPayload
  ): Promise<UpdateParticipantsResponseDTO>;

  /* delete */
  abstract deleteFile(sixId: string, fileId: string): Promise<DeleteFileResponseDTO>;

  /* favorite */
  abstract toggleFileFavorite(sixId: string, fileId: string): Promise<SixFileFavoriteDTO>;

  /* move */
  abstract moveItems(sixId: string, payload: MoveItemsPayloadDTO): Promise<void>;

  /* download tracking */
  abstract markFileDownloaded(sixId: string, fileId: string): Promise<void>;

  /* deleted-files cleanup */
  abstract fetchDeletedFiles(): Promise<DeletedFileEntry[]>;
}
